import React, { Suspense, useCallback, useEffect } from 'react'
import { observer } from 'mobx-react'
import { useAuth } from 'oidc-react'

import Spinner from 'Shared/atoms/Spinner'
import { usePasswordChangeRedirect } from 'Shared/hooks/usePasswordChangeRedirect'
import Modal from 'Shared/organisms/Modal'
import { AuthFields } from 'Shared/utils/auth'
import { addGlobalYupMethods } from 'Shared/utils/yupGlobalValidators'

import { UserType } from 'Core/interfaces/core'
import { redirectChecking } from 'Core/utils/auth'

import { AuthInstance } from './constants'
import Layouts from './Layouts'
import { useStores } from './stores'
import { LAYOUT_TYPE_USER_TYPE_DEPENDENCIES } from './utils'

addGlobalYupMethods()

// Map abbreviated auth values to full enum values
const normalizeUserType = (userType: string | undefined): UserType | undefined => {
	if (!userType) return undefined
	
	// Map backend abbreviated values to full enum values
	const userTypeMap: Record<string, UserType> = {
		'AP': UserType.ACCOUNTS_PAYABLE,
		[UserType.ACCOUNTS_PAYABLE]: UserType.ACCOUNTS_PAYABLE,
		[UserType.SUPPLIER]: UserType.SUPPLIER,
		[UserType.NEW_SUPPLIER]: UserType.NEW_SUPPLIER
	}
	
	return userTypeMap[userType] as UserType | undefined
}

const App: React.FC = () => {
	const auth = useAuth()
	const {
		authStore: { authData, setUserData },
		profileStore: { retrieveInitialData, profile }
	} = useStores()

	const signOutRedirectCallback = useCallback(() => AuthInstance.signOutRedirect(), [])
	const clearAuthStorage = useCallback(() => AuthInstance.clearAuthStorage(), [])

	usePasswordChangeRedirect(signOutRedirectCallback, clearAuthStorage)

	redirectChecking()

	useEffect(() => {
		if (authData || !auth.userData) {
			return
		}

		setUserData(auth.userData)
	}, [authData, setUserData, auth.userData])

	useEffect(() => {
		retrieveInitialData().catch(() => {})
	}, [retrieveInitialData])

	if (!auth.userData || !profile.value) {
		return <Spinner isFullWindow isBrandLogo />
	}

	const rawUserType = auth?.userData?.profile[AuthFields.userType] as string | undefined
	const userType = normalizeUserType(rawUserType)
	const layoutType = userType ? LAYOUT_TYPE_USER_TYPE_DEPENDENCIES[userType] : undefined

	if (process.env.NODE_ENV !== 'production') {
		console.log('[Supplier App] rawUserType:', rawUserType, 'normalizedUserType:', userType, 'layoutType:', layoutType)
	}

	return (
		<>
			<Suspense fallback={<Spinner isFullWindow isBrandLogo />}>
				<Layouts layoutType={layoutType} />
			</Suspense>
			<Modal />
		</>
	)
}

export default observer(App)
